import { forwardRef, type SelectHTMLAttributes } from 'react';
import clsx from 'clsx';

// Brand-styled native <select> with the platform chevron suppressed and a
// custom inline-SVG chevron inset from the right edge.
//
// Two variants:
//   block — full-width form-input shape (rounded-lg, bg-subtle). Use inside
//           forms / modals where the select reads as a normal input.
//   pill  — small filter/toggle shape (rounded-full, bg-elevated). Use as a
//           single-purpose selector that lives next to text or in a header.

type SelectVariant = 'block' | 'pill';

const CHEVRON =
  "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7077' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")";

const BASE =
  'appearance-none cursor-pointer focus:outline-none transition-colors bg-no-repeat';

const VARIANT: Record<SelectVariant, string> = {
  block:
    'w-full bg-subtle border border-border rounded-lg pl-3 pr-7 py-2 text-sm text-fg focus:border-green',
  pill: 'bg-elevated border border-border rounded-full pl-3 pr-7 h-8 text-xs font-medium text-fg-secondary hover:bg-overlay hover:text-fg hover:border-border-strong focus:border-border-strong tabular',
};

const CHEVRON_POSITION: Record<SelectVariant, string> = {
  block: 'bg-[length:12px_12px] bg-[position:right_0.75rem_center]',
  pill: 'bg-[length:12px_12px] bg-[position:right_0.75rem_center]',
};

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  variant?: SelectVariant;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { variant = 'block', className, style, children, ...rest },
  ref
) {
  return (
    <select
      ref={ref}
      {...rest}
      className={clsx(BASE, VARIANT[variant], CHEVRON_POSITION[variant], className)}
      style={{ backgroundImage: CHEVRON, ...style }}
    >
      {children}
    </select>
  );
});
